import React, { useEffect, useState } from 'react'
import QuestionDropDown from '../QuestionDropDown'
import Box from '@mui/material/Box'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import LinearScaleOption from './MultipleChoiceOptions'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import { type LinearQuestion, SurveyItemQuestionType, type ILinearObject } from '../../../types'
import { useDispatch } from 'react-redux'
import CreateQuestionCardWrapper from '../CreateQuestionCardWrapper'
import { StyledBox, StyledMultipleChoiceWrapper, StyledOptionWrapper, StyledQuestionWrapper, StyledTextField } from './styles'
import { ButtonSize, ButtonType } from '../../../../../components/GenericButton/styles'
import GenericButton from '../../../../../components/GenericButton'
import { useTheme } from '@mui/material/styles'
import { Typography } from '@mui/material'
import { blue, grey } from '../../../../../colors'
import { addFollowUpFreeResponseQuestionInSection, addSurveyQuestionInSection, type ISurveySection, setSelectedQuestion, updateBranchFilterAnswers, updateBranchFilterPrompt, updateQuestionInSection } from '../../../../../store/slices/SurveyTemplateSlice'
import { getEditableQuestion, getSelectedQuestion } from '../../../../../store/slices/SurveyTemplateSlice/selectors'
import QuestionFooter from '../../QuestionFooter'
import { v4 as uuidv4 } from 'uuid'
import { checkQuestionOptionHaveBranching, getUniqueChildBranchesDetails } from '../../../helpers'

interface CreateDescriptiveQuestionCardProps {
  questionIndex?: number
  sectionIndex?: number
  questionNumber?: number
  section: ISurveySection
  isAdminAddedQuestion?: boolean
  isCountComplete?: boolean
}

const CreateLinearScaleCard: React.FC<CreateDescriptiveQuestionCardProps> = ({ questionIndex = 0, sectionIndex = 0, questionNumber, section, isAdminAddedQuestion, isCountComplete }): JSX.Element => {
  const [weightError, setWeightError] = useState<string | null>(null)
  const [linearCardData, setlinearCardData] = useState<LinearQuestion>({
    itemId: uuidv4(),
    itemType: SurveyItemQuestionType.LINEAR_SCALE,
    prompt: '',
    object: [
      { option: 'Strongly Disagree', weight: 1 },
      { option: 'Disagree', weight: 2 },
      { option: 'Neutral', weight: 3 },
      { option: 'Agree', weight: 4 },
      { option: 'Strongly Agree', weight: 5 }
    ],
    isRequired: false,
    branchingFilters: null
  })

  const dispatch = useDispatch()
  const editableQuestion = getEditableQuestion()
  const selectedQuestion = getSelectedQuestion()
  const theme = useTheme()

  const [isReversed, setIsReversed] = useState<boolean>(
    (linearCardData.object[0]?.weight ?? 0) > (linearCardData.object[1]?.weight ?? 0)
  )
  useEffect(() => {
    setIsReversed(
      linearCardData.object[0]?.weight != null &&
      linearCardData.object[1]?.weight != null &&
      linearCardData.object[0].weight > linearCardData.object[1].weight
    )
  }, [linearCardData.object])

  const calculateProperIndex = (index: number): number => {
    return isReversed ? linearCardData.object.length - 1 - index : index
  }

  const calculateProperWeight = (index: number, newReversedState?: boolean, newLength?: number, isNaBeingRemoved?: boolean): number => {
    const properReversedState = newReversedState ?? isReversed
    const properObjectLength = newLength ?? linearCardData.object.length
    const optionObj = linearCardData.object[index]
    if (optionObj && optionObj.option === 'Not Applicable') return 'N/A' as unknown as number
    if (properReversedState && (!isNaBeingRemoved && hideNotApplicable)) return properObjectLength - index - 1
    else if (properReversedState) return properObjectLength - index
    return index + 1
  }

  useEffect(() => {
    if (editableQuestion != null) {
      setlinearCardData(editableQuestion?.question as LinearQuestion)
      return
    }
    selectedQuestion?.question && setlinearCardData(selectedQuestion?.question as LinearQuestion)
  }, [editableQuestion, dispatch])

  // Check for duplicate weights whenever linearCardData.object changes
  useEffect(() => {
    const weights = linearCardData.object.filter(option => option?.option !== 'Not Applicable').map(option => option.weight)
    const hasDuplicate = weights.length !== new Set(weights).size
    setWeightError(hasDuplicate ? 'Points must be unique for each option.' : null)
  }, [linearCardData.object])

  const handleQuestionTitle = (questionTitle: string): void => {
    // Change Question Prompt in child Branch Section Branching Filters
    if (linearCardData?.branchingFilters) {
      dispatch(updateBranchFilterPrompt({
        childBranches: getUniqueChildBranchesDetails(linearCardData.branchingFilters),
        prompt: questionTitle
      }))
    }

    setlinearCardData((prevState: LinearQuestion) => {
      return { ...prevState, prompt: questionTitle }
    })
  }

  const AddNewOption = (): void => {
    setlinearCardData((prevState: LinearQuestion) => {
      const newOptions = [...prevState?.object]
      if (isReversed) newOptions.unshift({ option: '', weight: calculateProperWeight(0) + 1 })
      else newOptions.push({ option: '', weight: calculateProperWeight(newOptions.length) - (hideNotApplicable ? 1 : 0) })
      const updatedData = newOptions?.sort((a: ILinearObject, b: ILinearObject) => {
        if (a.option === 'Not Applicable') return 1
        if (b.option === 'Not Applicable') return -1
        return 0
      })

      return { ...prevState, object: updatedData }
    })
  }

  const AddNewOptionNotApplicable = (): void => {
    setlinearCardData((prevState: LinearQuestion) => {
      const newOptions = [...prevState?.object]
      newOptions.push({ option: 'Not Applicable', weight: null })
      return { ...prevState, object: newOptions }
    })
  }

  const RemoveOption = (index: number): void => {
    // If Question Option have BranchSection don't Delete show Alert message
    if (checkQuestionOptionHaveBranching(index, linearCardData)) {
      return
    }

    setlinearCardData((prevState: LinearQuestion) => {
      const newOptions = [...prevState.object]
      const isNaBeingRemoved = newOptions[index].option === 'Not Applicable'

      // Check if weights are sequential (auto-generated) before deletion
      const optionsWithoutNa = prevState.object.filter(o => o.option !== 'Not Applicable')
      const weightsWithoutNa = optionsWithoutNa.map(o => o.weight).filter(w => w !== null)

      // Check if weights match exact sequential pattern (1,2,3... or n,n-1,n-2... for reversed)
      const expectedWeights = optionsWithoutNa.map((_, i) => isReversed ? optionsWithoutNa.length - i : i + 1)
      const isSequential = weightsWithoutNa.length === expectedWeights.length &&
        weightsWithoutNa.every((weight, i) => weight === expectedWeights[i])

      newOptions.splice(index, 1)

      // Only recalculate weights if they were exactly sequential, otherwise preserve custom weights
      const updatedOptions = isSequential
        ? newOptions.map((option, i) => {
          // Skip recalculation for 'Not Applicable' options
          if (option.option === 'Not Applicable') return option
          return { ...option, weight: calculateProperWeight(i, isReversed, newOptions.length, isNaBeingRemoved) }
        })
        : newOptions // Preserve existing weights

      return { ...prevState, object: updatedOptions }
    })
  }
  const handleRequired = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    setlinearCardData((prevState: any) => {
      return { ...prevState, isRequired: checked }
    })
  }

  const handleLabelChange = (optionIndex: number, newLabel: string): void => {
    const properIndex = calculateProperIndex(optionIndex)
    setlinearCardData((prevState: LinearQuestion) => {
      const newOptions = [...prevState.object]
      newOptions[properIndex] = { ...newOptions[properIndex], option: newLabel }

      // If Selected Option have branch section Update that as well
      const oldKey = prevState.object[properIndex]?.option
      const updatedBranchingFilters = prevState.branchingFilters?.map(filter =>
        filter.key === oldKey
          ? { ...filter, key: newLabel }
          : filter
      )
      // Update Child Branch section answers
      const payload = { oldKey, newKey: newLabel, parentQuestionId: prevState.itemId, parentSurveySectionId: section.surveySectionId }
      dispatch(updateBranchFilterAnswers(payload))
      return {
        ...prevState,
        object: newOptions,
        branchingFilters: updatedBranchingFilters ?? linearCardData.branchingFilters
      }
    })
  }

  const handlePointsChange = (optionIndex: number, newPoints: number): void => {
    const properIndex = calculateProperIndex(optionIndex)
    setlinearCardData((prevState: LinearQuestion) => {
      const newOptions = [...prevState.object]
      newOptions[properIndex] = { ...newOptions[properIndex], weight: newPoints }
      // Check for duplicate weights (excluding 'Not Applicable')
      const weights = newOptions.filter(option => option?.option !== 'Not Applicable').map(option => option.weight)
      const hasDuplicate = weights.length !== new Set(weights).size
      setWeightError(hasDuplicate ? 'Points must be unique for each option.' : null)
      return {
        ...prevState,
        object: newOptions
      }
    })
  }

  const getSortedOptions = (): ILinearObject[] => {
    const options = linearCardData.object.filter(o => o.option !== 'Not Applicable')
    const notApplicable = linearCardData.object.find(o => o.option === 'Not Applicable')
    const sorted = [...options].sort((a, b) => {
      const aWeight = a.weight ?? 0
      const bWeight = b.weight ?? 0
      return isReversed ? bWeight - aWeight : aWeight - bWeight
    })
    return notApplicable ? [...sorted, notApplicable] : sorted
  }

  const handleDuplicate = (): void => {
    // Prevent duplicating if there's a weight error
    if (weightError) {
      return
    }

    const data = getSortedOptions().map((question: ILinearObject) => ({ option: question.option, weight: question.weight }))
    const preparedQuestionWithWeight = {
      ...linearCardData,
      object: data
    }

    // Duplicate question without isDirty
    const duplicateQuestion = {
      itemId: uuidv4(),
      object: data,
      itemType: SurveyItemQuestionType.LINEAR_SCALE,
      prompt: '',
      isRequired: linearCardData.isRequired,
      branchingFilters: null
    }

    if (editableQuestion) {
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: preparedQuestionWithWeight }))
      dispatch(setSelectedQuestion({
        sectionIndex,
        questionType: SurveyItemQuestionType.LINEAR_SCALE,
        question: duplicateQuestion
      }))
    } else {
      // New question: save it first, then set up the duplicate
      dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question: preparedQuestionWithWeight }))
      dispatch(setSelectedQuestion({
        sectionIndex,
        questionType: SurveyItemQuestionType.LINEAR_SCALE,
        question: duplicateQuestion
      }))
    }
  }

  const handleAddFollowUp = (): void => {
    // Prevent adding follow-up if there's a weight error
    if (weightError) {
      return
    }

    const data = getSortedOptions().map((question: ILinearObject) => ({ option: question.option, weight: question.weight }))
    const preparedQuestionWithWeight = {
      ...linearCardData,
      object: data
    }

    if (editableQuestion) {
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: preparedQuestionWithWeight }))
      dispatch(addFollowUpFreeResponseQuestionInSection({ surveySectionId: section.surveySectionId, followUpParentQuestionIndex: questionIndex, questionTitle: linearCardData.prompt }))
      dispatch(setSelectedQuestion(null))
    }
  }

  const handleClickAway = (): void => {
    // Prevent saving if there's a weight error
    if (weightError) {
      return
    }

    const data = getSortedOptions().map((question: ILinearObject) => ({ option: question.option, weight: question.weight }))
    const preparedQuestionWithWeight = {
      ...linearCardData,
      object: data
    }

    if (editableQuestion) {
      dispatch(updateQuestionInSection({ surveySectionId: section.surveySectionId, questionIndex, updatedQuestion: preparedQuestionWithWeight }))
      return
    }

    dispatch(addSurveyQuestionInSection({ surveySectionId: section.surveySectionId, question: preparedQuestionWithWeight }))
    dispatch(setSelectedQuestion(null))
  }

  const hideNotApplicable = linearCardData?.object?.find(value => value.option === 'Not Applicable')

  const footerPropsData = {
    questionIndex,
    questionData: linearCardData,
    handleChange: handleRequired,
    handleDuplicate,
    handleClickAway,
    handleAddFollowUp,
    section,
    isAdminAddedQuestion,
    isCountComplete
  }

  const isQuestionHaveBranchFilters = !!linearCardData?.branchingFilters?.length

  const handleReverseScale = (): void => {
    setIsReversed((prev) => !prev)
    setlinearCardData((prevState) => {
      const options = prevState.object.filter(o => o.option !== 'Not Applicable')
      const notApplicable = prevState.object.find(o => o.option === 'Not Applicable')
      const reversedOptions = [...options].reverse()
      const newObject = notApplicable ? [...reversedOptions, notApplicable] : reversedOptions
      return { ...prevState, object: newObject }
    })
  }

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={handleClickAway}>
      <StyledBox>
        <CreateQuestionCardWrapper isAdminAddedQuestion={isAdminAddedQuestion} questionNumber={questionNumber} questionData={linearCardData} section={section}>
          <>
            <StyledQuestionWrapper >
              <StyledTextField value={linearCardData?.prompt} type='text' placeholder='Type your question...' autoFocus onChange={(e) => { handleQuestionTitle(e.target.value) }} />
              <QuestionDropDown section={section} sectionIndex={sectionIndex} questionIndex={questionIndex} isQuestionHaveBranchFilters={isQuestionHaveBranchFilters} />
            </StyledQuestionWrapper>
            <GenericButton
              label={'Flip Scale'}
              buttonType={ButtonType.Secondary}
              onClick={handleReverseScale}
              buttonSize={ButtonSize.Large}
              style={{ marginBottom: theme.spacing(3.75) }}
            />
            <StyledMultipleChoiceWrapper>
              {linearCardData?.object?.map((option: ILinearObject, index: number) => (
                <StyledOptionWrapper key={index}>
                  <LinearScaleOption
                    label={option.option}
                    optionIndex={calculateProperIndex(index)}
                    points={option.weight === null ? undefined : option.weight}
                    isNaFlipped={!!hideNotApplicable && isReversed}
                    onLabelChange={handleLabelChange}
                    onPointsChange={handlePointsChange}
                  />
                  <GenericButton
                    buttonSize={ButtonSize.Small}
                    buttonType={ButtonType.Ghost}
                    icon={<ClearIcon color='action' />}
                    onClick={() => { RemoveOption(index) }}
                    disabled={linearCardData?.object?.length <= 2} />
                </StyledOptionWrapper>
              ))}
              {weightError && (
                <Typography color="error" sx={{ mt: 2, fontSize: 14, fontWeight: 500 }}>
                  {weightError}
                </Typography>
              )}
            </StyledMultipleChoiceWrapper>

            <Box display='flex' gap={theme.spacing(0)} mt={theme.spacing(4)} alignItems='center'>
              <GenericButton
                buttonType={ButtonType.Ghost}
                startIcon={<AddIcon sx={{ fontWeight: 500 }} />}
                onClick={() => { AddNewOption() }}
                label='Add Option'
                buttonSize={ButtonSize.Medium}
                style={{
                  fontSize: '16px',
                  fontWeight: 500,
                  color: grey[850],
                  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`
                }}
              />

              {
                !hideNotApplicable &&
                <>
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: grey[850] }}>or</Typography>
                  <GenericButton
                    buttonType={ButtonType.Ghost}
                    onClick={() => { AddNewOptionNotApplicable() }}
                    label='Not applicable'
                    buttonSize={ButtonSize.Medium}
                    style={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: blue[500],
                      padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`

                    }}
                  />
                </>
              }
            </Box>
            <QuestionFooter {...footerPropsData} />
          </>
        </CreateQuestionCardWrapper>
      </StyledBox>
    </ClickAwayListener>
  )
}

export default CreateLinearScaleCard
